import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import { toast } from "react-hot-toast";
import Breadcrumbs from "../../../components/dashboard/breadcrumbs";
import { SeoHelmetWrapper, safeString } from "../../../components/Utils";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import { PMDialog } from "../../../components/utils/PMDialog";
import ProConfirmation from "../../../components/utils/ProConfirmation";
import { Notes } from "../../../components/dashboard/notes/notes";
import { dispatchWithToast } from "../../../components/Utils";
import {
  deleteOfferAsync,
  getOfferAsync,
  convertOfferToProjectAsync,
} from "../../../slices/offer";
import { getPdfOffer } from "../../../api/offer";
import OfferSummaryHeader from "../../../components/dashboard/offers/offer-summary-header";
import OfferEditDialog from "../../../components/dashboard/offers/offer-edit-dialog";
import OfferHelpDrawer from "../../../components/dashboard/offers/offer-help-drawer";
import OfferOverview from "../../../components/dashboard/offers/offer-overview";
import OfferHistory from "../../../components/dashboard/offers/offer-history";
import OfferApprovalTab from "../../../components/dashboard/offers/offer-approval-tab";
import OfferActivityTimeline from "../../../components/dashboard/offers/offer-activity-timeline";
import OfferCalculations from "../../../components/dashboard/offers/offer-calculations";
import OfferLinesTab from "../../../components/dashboard/offers/offer-lines-tab";
import { OfferViewportSummaryProvider } from "../../../components/dashboard/offers/offer-viewport-summary";
import OfferDocumentTab from "../../../components/dashboard/offers/offer-document-tab";
import OfferAttachmentsTab from "../../../components/dashboard/offers/offer-attachments-tab";
import OfferChecklistTab from "../../../components/dashboard/offers/offer-checklist-tab";
import { OfferSendDialog } from "../../../components/dashboard/offers/offer-send-dialog";
import OfferSubmitForReviewDialog from "../../../components/dashboard/offers/offer-submit-for-review-dialog";
import OfferCreateRevisionDialog from "../../../components/dashboard/offers/offer-create-revision-dialog";
import {
  OfferSaveAsDialog,
  OfferOverwriteDialog,
  OfferSaveAsTemplateDialog,
  OfferSaveToTemplateDialog,
} from "../../../components/dashboard/offers/offer-save-dialogs";
import OfferTemplateVersionHistoryDialog from "../../../components/dashboard/offers/offer-template-version-history";
import {
  isOfferFeatureEnabled,
  isOfferTemplateVersionsEnabled,
  resolveOfferActionErrorMessage,
} from "../../../components/dashboard/offers/utils";

import OfferReviewWorkspace from "../../../components/dashboard/offers/offer-review-workspace";
import OfferSetupPrompts from "../../../components/dashboard/offers/offer-setup-prompts";
import { flushOfferSaves, areOfferSavesDirty } from "../../../components/dashboard/offers/utils/offer-save-queue";
import { resolveOfferLayout } from "../../../components/dashboard/offers/utils/offer-layout";

const VALID_TABS = [
  "oversikt",
  "kalkyle",
  "tilbudslinjer",
  "dokument",
  "kontroll",
  "sjekkliste",
  "revisjoner",
  "godkjenning",
  "vedlegg",
  "historikk",
];

/** Friendly / legacy ?tab= aliases → canonical tab ids. */
const TAB_ALIASES = {
  kundevisning: "tilbudslinjer",
  lines: "tilbudslinjer",
  customer: "tilbudslinjer",
  calculation: "kalkyle",
  document: "dokument",
  attachments: "vedlegg",
  checklist: "sjekkliste",
  revisions: "revisjoner",
  approval: "godkjenning",
  history: "historikk",
  aktiviteter: "historikk",
  activity: "historikk",
  overview: "oversikt",
  review: "kontroll",
};

function resolveOfferTab(tabParam) {
  if (!tabParam) return "oversikt";
  const resolved = TAB_ALIASES[tabParam] || tabParam;
  return VALID_TABS.includes(resolved) ? resolved : "oversikt";
}

/**
 * Kalkyle tab — mounts the existing OfferCalculations flow inside a lightweight
 * Formik wrapper. offer-bug-p0: OfferCalculations now persists edits directly
 * against `/offer_calculations/:id` (see offer-calculations.js) — the Offer
 * controller's strong params never permitted `:calculations`, so the previous
 * `updateOfferAsync({ calculations }) + getOfferAsync` round-trip silently
 * dropped every edit and was the source of the PUT+GET storm.
 */
function OfferCalculationsTab({ offer }) {
  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <Formik
        enableReinitialize
        initialValues={{
          id: offer?.id,
          calculations: offer?.calculations || [],
        }}
        onSubmit={() => {}}
      >
        {(formik) => <OfferCalculations order={offer} formik={formik} />}
      </Formik>
    </div>
  );
}

export const Offer = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { isLoading, error, errors, offer, offerFeatureFlags } = useSelector(
    (store) => store.offers
  );

  const featureFlags = offer?.offer_feature_flags || offerFeatureFlags || {};
  const revisionsEnabled = isOfferFeatureEnabled(featureFlags, "offer_revisions");
  const checklistEnabled = isOfferFeatureEnabled(featureFlags, "offer_checklist");

  const [deletePromptOpen, setDeletePromptOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendLayout, setSendLayout] = useState("simple");
  useEffect(() => {
    setSendLayout(resolveOfferLayout(offer));
  }, [offer?.id, offer?.layout]);
  const [submitForReviewOpen, setSubmitForReviewOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [convertingToProject, setConvertingToProject] = useState(false);
  const [conversionPreview, setConversionPreview] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [overwriteOpen, setOverwriteOpen] = useState(false);
  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  const [saveToTemplateOpen, setSaveToTemplateOpen] = useState(false);
  const [templateVersionsOpen, setTemplateVersionsOpen] = useState(false);
  const [createRevisionOpen, setCreateRevisionOpen] = useState(false);
  const templateVersionsEnabled = isOfferTemplateVersionsEnabled(featureFlags);

  useEffect(() => {
    dispatch(getOfferAsync(id));
  }, [dispatch, id]);

  useEffect(() => {
    const guard = (event) => {
      if (!areOfferSavesDirty(id)) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [id]);

  useEffect(() => {
    const onClick = (event) => {
      if (!areOfferSavesDirty(id)) return;
      const anchor = event.target?.closest?.("a[href]");
      if (!anchor || event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const href = anchor.getAttribute("href") || "";
      if (!href.startsWith("/") || href.startsWith("//")) return;
      if (href.includes(`/dashboard/offers/${id}`)) return;
      const leave = window.confirm(t("offers.workspace.unsaved_leave"));
      if (!leave) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [id, t]);

  const tabParam = searchParams.get("tab");
  const activeTab = resolveOfferTab(tabParam);

  const handleTabChange = async (tabId) => {
    try { await flushOfferSaves(offer?.id); } catch { toast.error(t("offers.workspace.save_error")); return; }
    const canonicalTab = resolveOfferTab(tabId);
    // Await GET before remount. Customer view hits live /customer_presentation
    // (DB), while Kalkyle hydrates from Redux offer.calculations — a fire-and-
    // forget getOffer left Calc on a stale snapshot after Edit calc save.
    if (
      offer?.id &&
      (canonicalTab === "kalkyle" || canonicalTab === "tilbudslinjer")
    ) {
      await dispatch(getOfferAsync(offer.id));
    }
    const next = new URLSearchParams(searchParams);
    next.set("tab", canonicalTab);
    setSearchParams(next, { replace: true });
  };

  const handleDeleteItem = () => {
    dispatchWithToast(dispatch(deleteOfferAsync(offer?.id)), null, () => {
      setDeletePromptOpen(false);
      navigate("/dashboard/offers");
    });
  };

  const handlePdf = async (layout) => {
    if (!offer?.id) return;
    const resolvedLayout = layout || resolveOfferLayout(offer);
    setPdfLoading(true);
    try {
      await flushOfferSaves(offer.id);
      const response = await getPdfOffer({
        id: offer.id,
        params: { layout: resolvedLayout },
      });
      if (response?.pdf_url) {
        window.open(response.pdf_url, "_blank", "noopener,noreferrer");
      } else {
        toast.error(
          resolveOfferActionErrorMessage(t, response, "offers.history.pdf_failed")
        );
      }
    } catch (err) {
      console.error("Error generating PDF:", err);
      toast.error(t("offers.history.pdf_failed"));
    } finally {
      setPdfLoading(false);
    }
  };

  const handleConvert = async () => {
    if (!offer?.id || convertingToProject) return;
    if (offer?.project_id) {
      navigate(`/dashboard/projects/${offer.project_id}`);
      return;
    }
    setConversionPreview(false);
    setConvertingToProject(true);
    try {
      const result = await dispatch(
        convertOfferToProjectAsync({ id: offer.id })
      );
      if (convertOfferToProjectAsync.rejected.match(result)) {
        toast.error(
          resolveOfferActionErrorMessage(
            t,
            result.payload,
            "offers.convert.failed"
          )
        );
        return;
      }
      const projectId = result.payload?.project_id;
      toast.success(
        result.payload?.idempotent
          ? t("offers.convert.already_linked")
          : t("offers.convert.success")
      );
      if (projectId) {
        navigate(`/dashboard/projects/${projectId}`);
      } else {
        dispatch(getOfferAsync(offer.id));
      }
    } finally {
      setConvertingToProject(false);
    }
  };

  const handleCopy = () => {
    setSaveAsOpen(true);
  };

  // Flat primary nav (no 01–04 areas). Order matches preferred UX:
  // Oversikt → Kalkyle → Kundevisning → Dokument → … → Vedlegg → Historikk.
  // Kontroll + Sjekkliste stay for feature parity / deep links.
  const tabs = useMemo(() => {
    const list = [
      { id: "oversikt", label: t("offers.tabs.overview") },
      { id: "kalkyle", label: t("offers.tabs.calculation") },
      { id: "tilbudslinjer", label: t("offers.tabs.lines") },
      { id: "dokument", label: t("offers.tabs.document") },
      { id: "kontroll", label: t("offers.workspace.review") },
      {
        id: "sjekkliste",
        label: t("offers.tabs.checklist"),
        disabled: checklistEnabled === false && Object.keys(featureFlags).length > 0,
        disabledHint: t("offers.checklist.unavailable"),
      },
    ];

    // Hide Revisjoner when flag is explicitly off; Historikk is always available.
    // While flags are still loading (empty object), keep the tab visible.
    if (revisionsEnabled || Object.keys(featureFlags).length === 0) {
      list.push({
        id: "revisjoner",
        label: t("offers.tabs.revisions"),
      });
    }

    list.push(
      {
        id: "godkjenning",
        label: t("offers.tabs.approval"),
        disabled: revisionsEnabled === false && Object.keys(featureFlags).length > 0,
        disabledHint: t("offers.approval.unavailable"),
      },
      { id: "vedlegg", label: t("offers.tabs.attachments") },
      { id: "historikk", label: t("offers.tabs.history") }
    );

    return list;
  }, [t, revisionsEnabled, checklistEnabled, featureFlags]);

  const renderTab = () => {
    switch (activeTab) {
      case "kontroll":
        return <OfferReviewWorkspace offer={offer} onNavigate={handleTabChange} onPreview={handlePdf} onSend={(layout) => { setSendLayout(layout); setSendDialogOpen(true); }} />;
      case "oversikt":
        return (
          <OfferOverview
            offer={offer}
            featureFlags={featureFlags}
            isLoading={isLoading}
          />
        );
      case "tilbudslinjer":
        return (
          <OfferLinesTab key={offer?.id} offer={offer} onPrint={handlePdf} />
        );
      case "dokument":
        return <OfferDocumentTab key={offer?.id} offer={offer} />;
      case "vedlegg":
        return <OfferAttachmentsTab offer={offer} />;
      case "sjekkliste":
        return <OfferChecklistTab offer={offer} />;
      case "revisjoner":
        return (
          <OfferHistory
            offer={offer}
            refreshKey={historyRefreshKey}
            onSubmitForReview={() => setSubmitForReviewOpen(true)}
            onSendOffer={() => handleTabChange("kontroll")}
          />
        );
      case "godkjenning":
        return (
          <OfferApprovalTab
            offer={offer}
            refreshKey={historyRefreshKey}
            onSubmitForReview={() => setSubmitForReviewOpen(true)}
            onSendOffer={() => handleTabChange("kontroll")}
            onCreateRevision={
              revisionsEnabled ? () => setCreateRevisionOpen(true) : undefined
            }
          />
        );
      case "historikk":
        return (
          <OfferActivityTimeline
            offer={offer}
            refreshKey={historyRefreshKey}
          />
        );
      case "kalkyle":
      default:
        return <OfferCalculationsTab key={offer?.id} offer={offer} />;
    }
  };

  const pageTitle = safeString(offer?.displayTitle || t("offers.banner.offer"));

  if (isLoading && !offer?.id) {
    return <ResourceLoading />;
  }

  if (error && errors) {
    return <ResourceError errors={errors} />;
  }

  const fillViewport = ["kalkyle", "dokument", "tilbudslinjer"].includes(
    activeTab
  );
  const dockSummary = ["kalkyle", "tilbudslinjer"].includes(activeTab);

  return (
    <>
      <SeoHelmetWrapper title={pageTitle} />
      <div
        className={
          fillViewport
            ? "offer-editor offer-editor--fill flex h-[calc(100dvh-4rem)] max-h-[calc(100dvh-4rem)] min-h-0 flex-col overflow-hidden"
            : "flex min-h-0 flex-col"
        }
      >
      <OfferViewportSummaryProvider enabled={dockSummary}>
        <div className="shrink-0 bg-white">
          <Breadcrumbs currentPageTitle={pageTitle} />
        </div>

        <OfferSetupPrompts
          forceSignature={["dokument", "kontroll"].includes(activeTab)}
        />

        <div className="shrink-0">
          <OfferSummaryHeader
            offer={offer}
            featureFlags={featureFlags}
            isLoading={isLoading}
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            onEdit={() => setEditDialogOpen(true)}
            onSubmitForReview={() => setSubmitForReviewOpen(true)}
            onSendOffer={() => handleTabChange("kontroll")}
            onPdf={handlePdf}
            onConvert={() => offer?.project_id ? handleConvert() : setConversionPreview(true)}
            convertingToProject={convertingToProject}
            pdfLoading={pdfLoading}
            onCopy={handleCopy}
            onSaveAs={() => setSaveAsOpen(true)}
            onOverwrite={() => setOverwriteOpen(true)}
            onSaveAsTemplate={
              templateVersionsEnabled ? () => setSaveAsTemplateOpen(true) : undefined
            }
            onSaveToTemplate={
              templateVersionsEnabled && !offer?.is_template
                ? () => setSaveToTemplateOpen(true)
                : undefined
            }
            onViewTemplateVersions={
              templateVersionsEnabled && offer?.is_template
                ? () => setTemplateVersionsOpen(true)
                : undefined
            }
            onCreateRevision={
              revisionsEnabled ? () => setCreateRevisionOpen(true) : undefined
            }
            onDelete={() => setDeletePromptOpen(true)}
            onHelp={() => setHelpOpen(true)}
            notesSlot={
              offer?.id ? (
                <Notes module="offer" module_param={offer?.id} />
              ) : null
            }
          />
        </div>

        {/*
          Claude IA: Kalkyle / Dokument / Tilbudslinjer (and Historikk) are
          full-bleed workspaces; other tabs stay constrained like overview.
          Fill-viewport tabs dock the totals bar to the screen bottom
          (full content width under dashboard chrome), not the inner card.
        */}
        <div
          className={
            ["kalkyle", "dokument", "historikk", "tilbudslinjer"].includes(
              activeTab
            )
              ? "flex min-h-0 w-full flex-1 flex-col overflow-hidden"
              : "mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6"
          }
        >
          {renderTab()}
        </div>
      </OfferViewportSummaryProvider>
      </div>

      <PMDialog open={conversionPreview} onClose={() => setConversionPreview(false)} title="offers.workspace.conversion" size="md">
        <div className="space-y-4 p-4"><p>{t("offers.workspace.conversion_hint")}</p><dl><dt>{t("offers.create.customer")}</dt><dd>{offer?.customer?.name || "—"}</dd><dt>{t("offers.workspace.customer_total")}</dt><dd>{offer?.accepted_revision?.subtotal_amount ?? offer?.subtotal_amount ?? offer?.total_amount ?? "—"} {offer?.currency || "NOK"}</dd></dl><button type="button" className="offer-workspace-primary rounded-lg px-4 py-2" onClick={handleConvert}>{t("offers.workspace.conversion_confirm")}</button></div>
      </PMDialog>
      <OfferEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        offer={offer}
        onSaved={() => setHistoryRefreshKey((key) => key + 1)}
      />

      <OfferHelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />

      <OfferSubmitForReviewDialog
        open={submitForReviewOpen}
        onClose={() => setSubmitForReviewOpen(false)}
        offer={offer}
        featureFlags={featureFlags}
        onRefresh={async (response) => {
          const offerId = offer?.id || response?.offer?.id;
          if (offerId) {
            await dispatch(getOfferAsync(offerId));
          }
          setHistoryRefreshKey((key) => key + 1);
        }}
      />

      <OfferSendDialog
        documentLayout={sendLayout}
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        offer={offer}
        isLoading={isLoading}
        featureFlags={featureFlags}
        onRefresh={async (response) => {
          const offerId = offer?.id || response?.offer?.id;
          if (offerId) {
            await dispatch(getOfferAsync(offerId));
          }
          setHistoryRefreshKey((key) => key + 1);
        }}
      />

      <ProConfirmation
        severity="confirmation"
        open={deletePromptOpen}
        message={t("offers.delete.confirm")}
        action="delete"
        module="offer"
        yesCallback={handleDeleteItem}
        cancelCallback={() => setDeletePromptOpen(false)}
      />

      <OfferSaveAsDialog
        open={saveAsOpen}
        onClose={() => setSaveAsOpen(false)}
        offer={offer}
      />
      <OfferCreateRevisionDialog
        open={createRevisionOpen}
        onClose={() => setCreateRevisionOpen(false)}
        offer={offer}
        featureFlags={featureFlags}
        onCopyOffer={() => setSaveAsOpen(true)}
        onRefresh={async () => {
          setHistoryRefreshKey((key) => key + 1);
        }}
      />
      <OfferOverwriteDialog
        open={overwriteOpen}
        onClose={() => setOverwriteOpen(false)}
        offer={offer}
        onOverwritten={(updated) => {
          if (updated?.id) dispatch(getOfferAsync(updated.id));
        }}
      />
      <OfferSaveAsTemplateDialog
        open={saveAsTemplateOpen}
        onClose={() => setSaveAsTemplateOpen(false)}
        offer={offer}
      />
      <OfferSaveToTemplateDialog
        open={saveToTemplateOpen}
        onClose={() => setSaveToTemplateOpen(false)}
        offer={offer}
      />
      <OfferTemplateVersionHistoryDialog
        open={templateVersionsOpen}
        onClose={() => setTemplateVersionsOpen(false)}
        offerId={offer?.id}
        onRestored={() => {
          if (offer?.id) dispatch(getOfferAsync(offer.id));
        }}
      />
    </>
  );
};
